import { useState, useMemo } from 'react';
import FilterCategory from './FilterCategory';
import './Filter.css';
import { sortObjectsByName } from '../utils/sortObjectsByName';

// FilterMenu component to show categories and their subcategories with expandable lists

function FilterMenu({ categories, subcategories, showCategory, setShowCategory, categoryLocations, subcategoryLocations, onSelectLocation }) {

  // Sort categories and subcategories alphabetically
  const sortedCategories = useMemo(() => {
    return sortObjectsByName([...categories]);
  }, [categories]);

  const sortedSubcategories = useMemo(() => {
    return sortObjectsByName([...subcategories]);
  }, [subcategories]);

  // Track which subcategories are open/closed
  const initialOpenCategories = {};
  sortedCategories.forEach(category => {
    initialOpenCategories[category.id] = false;
  });
  const [openCategories, setOpenCategories] = useState(initialOpenCategories);
  const [openSubcategories, setOpenSubcategories] = useState({});

  // Keep track of open (sub-)categories
  let anyCategoryOpen = false;
  let anySubcategoryOpen = false;

  // Toggle open/close state for a category
  const toggleOpenCategory = (catId, next) => {
    setOpenCategories((prev) => ({
      ...prev,
      [catId]: typeof next === 'boolean' ? next : !prev[catId],
    }));
  };

  // Toggle open/close state for a subcategory
  const toggleOpenSub = (subId, next) => {
    setOpenSubcategories((prev) => ({
      ...prev,
      [subId]: typeof next === 'boolean' ? next : !prev[subId],
    }));
  };

  // Group subcategories by their parent category
  const subcategoriesByCategory = useMemo(() => {
    return sortedSubcategories.reduce((acc, sub) => {
      const catId = sub.category?.id;
      if (!catId) return acc;
      if (!acc[catId]) acc[catId] = [];
      acc[catId].push(sub);
      return acc;
    }, {});
  }, [sortedSubcategories]);


  // Check if any (sub-)category is open
  for (const key in openCategories) {
    if (openCategories[key]) {
      anyCategoryOpen = true;
      break;
    }
  }
  for (const key in openSubcategories) {
    if (openSubcategories[key]) {
      anySubcategoryOpen = true;
      break;
    }
  }

  function onChange(id, checked, type, sub = null) {
    setShowCategory(prev => {
      const updated = {...prev};
      if (type === "category") {
        // Set the category checkbox
        updated[id] = checked;

        // Set the "noSubcategory" subcatgeory checkbox based on the category checkbox
        updated[`cat-${id}-noSubcategory`] = checked;

        // Get all the subcategories (if they exist) and set all subcategory checkboxes
        // based on the category checkbox
        const subs = subcategoriesByCategory[id] || [];
        subs.forEach(sub => {
          updated[sub.id] = checked;
        });
      } else if (type === "noSubcategory") {
        // Set the "subcategory" checkbox for the markers that have no subcategory
        updated[`cat-${id}-noSubcategory`] = checked;

        // Adjust category checkbox
        const subs = subcategoriesByCategory[id] || [];
        const allSubsChecked = subs.every(s => updated[s.id]);
        const ownChecked = updated[`cat-${id}-noSubcategory`];

        // If all subcategories and markers with no subcategories are active,
        // category is also active
        updated[id] = allSubsChecked && ownChecked;
      } else if (type === "subcategory") {
        updated[sub.id] = checked;
        const subs = subcategoriesByCategory[sub.category.id] || [];

        // Get current state of markers without a subcategory
        const ownChecked = updated[`cat-${sub.category.id}-noSubcategory`] ?? true;

        // Check if all subcategories are active
        const allSubsChecked = subs.every(s => !!updated[s.id]);

        // Set category active only if all subcategories AND the noSubcategory checkbox are active
        updated[sub.category.id] = allSubsChecked && !!ownChecked;
      }
      return updated;
    });
  }

  return (
    <div className={`Filters ${anyCategoryOpen || anySubcategoryOpen ? 'expanded' : 'compact'}`}>
      {sortedCategories.map((cat) => {
        const subs = subcategoriesByCategory[cat.id] || [];
        const isOpen = !!openCategories[cat.id];

        // Locations with no subcategory
        const noSubcategory = (categoryLocations?.[cat.id] || []).filter(s => !s.subcategory);

        // Total number of locations (own + subcategories)
        const allLocationsCount = noSubcategory.length + subs.reduce((acc, sub) => acc + (subcategoryLocations?.[sub.id]?.length || 0), 0);

        return (
          <div key={cat.id} className="FilterCategoryWrapper">

            {/* Category row */}
            <FilterCategory
              id={cat.id}
              name={cat.name}
              color={cat.color}
              count={allLocationsCount}
              checked={showCategory[cat.id] ?? true}
              onChange={(checked) => onChange(cat.id, checked, 'category')}
              showToggle={true}
              isOpen={isOpen}
              onToggle={(next) => toggleOpenCategory(cat.id, next)}
            />

            {/* Subcategories + locations inside the category */}
            <div className={`Subcategories ${isOpen ? 'open' : 'closed'}`}>

              {/* Locations with no subcategory */}
              {noSubcategory.length > 0 && (
                <div className="SubItemWrapper has-locations">
                  <FilterCategory
                    id={`cat-${cat.id}-noSubcategory`}
                    name="Stationen ohne Unterkategorie"
                    icon="⬜"
                    count={noSubcategory.length}
                    checked={showCategory[`cat-${cat.id}-noSubcategory`] ?? true}
                    onChange={(checked) => onChange(cat.id, checked, 'noSubcategory')}
                    showToggle={true}
                    isOpen={!!openSubcategories[`cat-${cat.id}-noSubcategory`]}
                    onToggle={(next) => toggleOpenSub(`cat-${cat.id}-noSubcategory`, next)}
                  />
                  {openSubcategories[`cat-${cat.id}-noSubcategory`] && (
                    <ul className="LocationList">
                      {sortObjectsByName(noSubcategory).map(location => (
                        <li 
                          key={location.id} 
                          onClick={() => {
                            const noSubId = `cat-${cat.id}-noSubcategory`;
    
                            setShowCategory(prev => {
                              const updated = { ...prev };
                              
                              // Show noSubcategory markers if they are invisible
                              if (!updated[noSubId]) {
                                updated[noSubId] = true;
                              }

                              // Check if all subcategories are visible
                              const subs = subcategoriesByCategory[cat.id] || [];
                              const allSubsChecked = subs.every(s => updated[s.id]);
                              
                              // If all subcategories and noSubcategory are visible, set the whole
                              // category to visible
                              if (allSubsChecked && updated[noSubId]) {
                                updated[cat.id] = true;
                              }

                              return updated;
                            });
                            onSelectLocation(location);
                          }}
                        >
                          {location.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Render each subcategory */}
              {subs.map((sub) => {
                const subLocations = subcategoryLocations?.[sub.id] || [];
                const subOpen = !!openSubcategories[sub.id];
                return (
                  <div key={sub.id} className={`SubItemWrapper ${subOpen && subLocations.length > 0 ? 'has-locations' : ''}`}>
                    <FilterCategory
                      id={sub.id}
                      name={sub.name}
                      count={subLocations.length}
                      icon={sub.icon}
                      checked={showCategory[sub.id] ?? true}
                      onChange={(checked) => onChange(cat.id, checked, 'subcategory', sub)}
                      showToggle={subLocations.length > 0}
                      isOpen={subOpen}
                      onToggle={(next) => toggleOpenSub(sub.id, next)}
                    />
                    {subOpen && subLocations.length > 0 && (
                      <ul className="LocationList">
                        {sortObjectsByName(subLocations).map(location => (
                          <li 
                            key={location.id}
                            onClick={() => {
                              const subId = location.subcategory.id;
                              const catId = cat.id;
                              const noSubId = `cat-${catId}-noSubcategory`;

                              setShowCategory(prev => {
                                const updated = { ...prev };

                                // Show subcategory markers if they are invisible
                                if (subId && !updated[subId]) {
                                  updated[subId] = true;
                                }

                                // Check if noSubcategory visible (if it exists)
                                const hasNoSub = !!categoryLocations?.[catId]?.some(loc => !loc.subcategory);
                                const ownChecked = hasNoSub ? (updated[noSubId] ?? true) : true;

                                // Check all subcategories
                                const subs = subcategoriesByCategory[catId] || [];
                                const allSubsChecked = subs.every(s => updated[s.id]);

                                // If all subcategories and noSubcategory are visible, set the whole
                                // category to visible
                                if (allSubsChecked && ownChecked) {
                                  updated[catId] = true;
                                }

                                return updated;
                              });
                              onSelectLocation(location);
                            }}
                          >
                            {location.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default FilterMenu;